import React, { useState, useEffect, useRef } from 'react';
import {
  Layers,
  ChevronLeft,
  ChevronRight,
  FlaskRound as Flask,
  Microscope,
  Cog,
  Zap,
  Hexagon,
  Monitor,
  Cylinder,
  PaintBucket,
  Info,
  CheckCircle2,
  ArrowRight,
  Atom,
  ArrowDownCircle,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import Footer from '../components/ui/Footer';
import { Helmet } from 'react-helmet-async';
import {
  MediaController,
  MediaControlBar,
  MediaPlayButton,
  MediaTimeRange,
  MediaFullscreenButton,
  MediaSeekBackwardButton,
  MediaSeekForwardButton,
} from 'media-chrome/react';
import { isVivatechBannerActive } from '@/components/VivatechBanner';

function Materials() {
  const { t } = useTranslation();

  const headerCards = [
    {
      title: t('ms4materials.headerCards.virtualDesign.title'),
      description: t('ms4materials.headerCards.virtualDesign.description'),
      icon: (
        <Flask className="h-8 w-8 text-blue-200 transition-transform group-hover:scale-110" />
      ),
      image: '/logos/thinfilmengineering.png',
    },
    {
      title: t('ms4materials.headerCards.molecularDynamics.title'),
      description: t('ms4materials.headerCards.molecularDynamics.description'),
      icon: (
        <Microscope className="h-8 w-8 text-blue-200 transition-transform group-hover:scale-110" />
      ),
      image: '/logos/resultatSimulationMaterials.png',
    },
    {
      title: t('ms4materials.headerCards.structuralOptimization.title'),
      description: t(
        'ms4materials.headerCards.structuralOptimization.description'
      ),
      icon: (
        <Hexagon className="h-8 w-8 text-blue-200 transition-transform group-hover:scale-110" />
      ),
      image: '/logos/structuralOptimization.png',
    },
    {
      title: t('ms4materials.headerCards.aiDiscovery.title'),
      description: t('ms4materials.headerCards.aiDiscovery.description'),
      icon: (
        <Zap className="h-8 w-8 text-blue-200 transition-transform group-hover:scale-110" />
      ),
      image: '/logos/aimaterial.jpg',
    },
  ];

  const categories = [
    {
      title: t('ms4materials.categories.designDiscovery.title'),
      features: [
        {
          name: t(
            'ms4materials.categories.designDiscovery.materialsDesign.name'
          ),
          description: t(
            'ms4materials.categories.designDiscovery.materialsDesign.description'
          ),
          icon: (
            <Flask className="relative z-10 mx-auto mb-4 h-8 w-8 text-blue-600" />
          ),
          image:
            'https://images.unsplash.com/photo-1532187863486-abf9dbad1b69?auto=format&fit=crop&q=80',
        },
        {
          name: t(
            'ms4materials.categories.designDiscovery.polymerScience.name'
          ),
          description: t(
            'ms4materials.categories.designDiscovery.polymerScience.description'
          ),
          icon: (
            <Microscope className="relative z-10 mx-auto mb-4 h-8 w-8 text-blue-600" />
          ),
          image:
            'https://images.unsplash.com/photo-1614935151651-0bea6508db6b?auto=format&fit=crop&q=80',
        },
        {
          name: t(
            'ms4materials.categories.designDiscovery.compositeMaterials.name'
          ),
          description: t(
            'ms4materials.categories.designDiscovery.compositeMaterials.description'
          ),
          icon: (
            <Layers className="relative z-10 mx-auto mb-4 h-8 w-8 text-blue-600" />
          ),
          image:
            'https://images.unsplash.com/photo-1635070041078-e363dbe005cb?auto=format&fit=crop&q=80',
        },
      ],
    },
    {
      title: t('ms4materials.categories.industrialApplications.title'),
      features: [
        {
          name: t(
            'ms4materials.categories.industrialApplications.metalAlloys.name'
          ),
          description: t(
            'ms4materials.categories.industrialApplications.metalAlloys.description'
          ),
          icon: (
            <Cog className="relative z-10 mx-auto mb-4 h-8 w-8 text-blue-600" />
          ),
          image:
            'https://images.unsplash.com/photo-1533106497176-45ae19e68ba2?auto=format&fit=crop&q=80',
        },
        {
          name: t(
            'ms4materials.categories.industrialApplications.catalystsDesign.name'
          ),
          description: t(
            'ms4materials.categories.industrialApplications.catalystsDesign.description'
          ),
          icon: (
            <Zap className="relative z-10 mx-auto mb-4 h-8 w-8 text-blue-600" />
          ),
          image:
            'https://images.unsplash.com/photo-1581093458791-9f3c3900df4b?auto=format&fit=crop&q=80',
        },
        {
          name: t(
            'ms4materials.categories.industrialApplications.surfaceCoatings.name'
          ),
          description: t(
            'ms4materials.categories.industrialApplications.surfaceCoatings.description'
          ),
          icon: (
            <PaintBucket className="relative z-10 mx-auto mb-4 h-8 w-8 text-blue-600" />
          ),
          image:
            'https://images.unsplash.com/photo-1562751362-404243c2eea3?auto=format&fit=crop&q=80',
        },
      ],
    },
    {
      title: t('ms4materials.categories.advancedTechnologies.title'),
      features: [
        {
          name: t(
            'ms4materials.categories.advancedTechnologies.electronicMaterials.name'
          ),
          description: t(
            'ms4materials.categories.advancedTechnologies.electronicMaterials.description'
          ),
          icon: (
            <div className="relative z-10">
              <Monitor className="mx-auto mb-4 h-12 w-12 text-blue-600" />
              <div className="absolute -right-1 -top-1 h-4 w-4 animate-pulse rounded-full bg-blue-200"></div>
            </div>
          ),
          image:
            'https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&q=80',
        },
        {
          name: t(
            'ms4materials.categories.advancedTechnologies.nanomaterials.name'
          ),
          description: t(
            'ms4materials.categories.advancedTechnologies.nanomaterials.description'
          ),
          icon: (
            <div className="relative z-10">
              <Hexagon className="mx-auto mb-4 h-12 w-12 text-blue-600" />
              <div className="absolute -bottom-1 -right-1 h-4 w-4 animate-pulse rounded-full bg-blue-200 delay-100"></div>
            </div>
          ),
          image:
            'https://images.unsplash.com/photo-1616440347437-b1c73416efc2?auto=format&fit=crop&q=80',
        },
        {
          name: t(
            'ms4materials.categories.advancedTechnologies.fuelCellMaterials.name'
          ),
          description: t(
            'ms4materials.categories.advancedTechnologies.fuelCellMaterials.description'
          ),
          icon: (
            <div className="relative z-10">
              <Cylinder className="mx-auto mb-4 h-12 w-12 text-blue-600" />
              <div className="absolute -left-1 -top-1 h-4 w-4 animate-pulse rounded-full bg-blue-200 delay-200"></div>
            </div>
          ),
          image:
            'https://images.unsplash.com/photo-1564933288282-7fde2b230946?auto=format&fit=crop&q=80',
        },
      ],
    },
  ];
  const [currentCategory, setCurrentCategory] = useState(0);
  const [slideDirection, setSlideDirection] = useState<'left' | 'right'>(
    'right'
  );
  const [isAnimating, setIsAnimating] = useState(false);
  const videoContainerRef = useRef<HTMLDivElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    const timer = setInterval(() => {
      if (!isAnimating) {
        setSlideDirection('right');
        setCurrentCategory((prev) => (prev + 1) % categories.length);
      }
    }, 7000);
    return () => clearInterval(timer);
  }, [isAnimating]);

  const handleCategoryChange = (
    direction: 'left' | 'right',
    index?: number
  ) => {
    if (isAnimating) return;

    setIsAnimating(true);
    setSlideDirection(direction);

    if (typeof index === 'number') {
      setCurrentCategory(index);
    } else {
      setCurrentCategory((prev) => {
        if (direction === 'right') {
          return (prev + 1) % categories.length;
        } else {
          return (prev - 1 + categories.length) % categories.length;
        }
      });
    }

    setTimeout(() => {
      setIsAnimating(false);
    }, 500);
  };

  const getSlideClassName = (index: number) => {
    if (index === currentCategory) {
      return 'opacity-100 translate-x-0';
    }

    if (slideDirection === 'right') {
      return index < currentCategory
        ? 'opacity-0 -translate-x-full'
        : 'opacity-0 translate-x-full';
    } else {
      return index > currentCategory
        ? 'opacity-0 translate-x-full'
        : 'opacity-0 -translate-x-full';
    }
  };

  const solutionsSection = useRef<HTMLDivElement>(null);

  const handleExploreSolutions = () => {
    requestAnimationFrame(() => {
      solutionsSection.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    });
  };

  const [showVideoContent, setShowVideoContent] = useState(false);
  const videoSectionRef = useRef<HTMLDivElement>(null);
  const videoSectionWithTitleRef = useRef<HTMLDivElement>(null);

  const handleScrollToVideo = () => {
    setShowVideoContent(true);

    requestAnimationFrame(() => {
      videoSectionWithTitleRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    });
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        const ratio = entry.intersectionRatio;

        setShowVideoContent((prev) => {
          if (!prev && ratio > 0.9) {
            return true;
          }

          if (prev && ratio < 0.7) {
            return false;
          }

          return prev;
        });
      },
      {
        threshold: [0, 0.25, 0.5, 0.7, 0.9, 1],
      }
    );

    if (videoSectionRef.current) {
      observer.observe(videoSectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <>
      <Helmet>
        <title>{t('ms4materials.metadata.title')}</title>
        <meta
          name="description"
          content={t('ms4materials.metadata.description')}
        />
      </Helmet>
      <div>
        <div className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-600 via-blue-700 to-blue-900">
            <div
              className="absolute inset-0"
              style={{
                backgroundImage:
                  'radial-gradient(circle at 2px 2px, rgba(255,255,255,0.15) 1px, transparent 0)',
                backgroundSize: '40px 40px',
              }}
            ></div>
          </div>

          {/* Floating Elements */}
          <div className="absolute inset-0 overflow-hidden">
            {[...Array(5)].map((_, i) => (
              <div
                key={i}
                className="animate-float absolute"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  animation: `float ${10 + i * 2}s ease-in-out infinite`,
                  animationDelay: `${i * 0.5}s`,
                }}
              >
                <Atom className="h-24 w-24 rotate-45 text-white/10" />
              </div>
            ))}
          </div>

          {/* Content */}
          <div className="relative mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2">
              <div className="space-y-8">
                <div className="flex items-center space-x-4">
                  <div className="rounded-2xl bg-white/10 p-4 backdrop-blur-sm">
                    <Layers className="h-12 w-12 text-white" />
                  </div>
                </div>

                <div>
                  <h2 className="mb-6 text-5xl font-bold leading-tight text-white">
                    {t('ms4materials.title')}
                    <span className="block text-blue-200">
                      {t('ms4materials.subtitle')}
                    </span>
                  </h2>
                  <p className="text-xl leading-relaxed text-blue-50">
                    {t('ms4materials.description')}
                  </p>
                </div>

                <div className="flex items-center space-x-6">
                  <button
                    onClick={handleExploreSolutions}
                    className="group flex items-center space-x-2 rounded-full border border-white/30 bg-white/5 px-6 py-3 text-white backdrop-blur-sm transition-all duration-300 hover:bg-white/10"
                  >
                    <span>{t('ms4materials.exploreSolutions')}</span>
                    <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
                  </button>
                  <div className="flex -space-x-4">
                    {[...Array(4)].map((_, i) => (
                      <div
                        key={i}
                        className="flex h-12 w-12 items-center justify-center rounded-full border-2 border-white/20 bg-gradient-to-br from-blue-400 to-blue-600"
                      >
                        <Microscope className="h-6 w-6 text-white" />
                      </div>
                    ))}
                    <div className="flex h-12 w-12 items-center justify-center rounded-full border-2 border-white/20 bg-white/10 font-medium text-white backdrop-blur-sm">
                      +8
                    </div>
                  </div>
                </div>
              </div>

              <div className="relative">
                <div className="absolute inset-0 rotate-6 transform rounded-3xl bg-gradient-to-br from-blue-400/30 to-blue-600/30 backdrop-blur-sm"></div>
                <div className="relative rounded-3xl border border-white/10 bg-gradient-to-br from-green-500/10 to-green-700/10 p-6 backdrop-blur-sm">
                  <div className="grid grid-cols-2 gap-6">
                    {headerCards.map((card, index) => (
                      <div
                        key={index}
                        className="group relative overflow-hidden rounded-xl transition-all duration-500 hover:shadow-2xl hover:shadow-blue-500/20"
                      >
                        {/* Background Image */}
                        <div
                          className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-110"
                          style={{
                            backgroundImage: `url(${card.image})`,
                            backgroundBlendMode: 'overlay',
                          }}
                        />

                        {/* Gradient Overlay */}
                        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/90 to-blue-600/70 opacity-90 transition-opacity duration-500 group-hover:opacity-75" />

                        {/* Content */}
                        <div className="relative z-10 flex h-full flex-col justify-between p-4">
                          <div>
                            <div className="mb-4 w-fit rounded-lg bg-white/10 p-3 backdrop-blur-sm transition-colors duration-300 group-hover:bg-white/20">
                              {card.icon}
                            </div>
                            <h3 className="mb-2 text-lg font-semibold text-white transition-colors duration-300 group-hover:text-blue-200">
                              {card.title}
                            </h3>
                            <p className="text-sm text-blue-100 transition-colors duration-300 group-hover:text-white/90">
                              {card.description}
                            </p>
                          </div>

                          {/* Hover Effect */}
                          <div className="flex translate-y-2 transform items-center text-blue-200 opacity-0 transition-all duration-500 group-hover:translate-y-0 group-hover:opacity-100"></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="-mt-20 py-4">
          <div className="mx-auto max-w-7xl px-4 text-center sm:px-6 lg:px-8">
            <ArrowDownCircle
              onClick={handleScrollToVideo}
              className="mx-auto h-12 w-12 animate-bounce cursor-pointer text-white/50 hover:text-white/70"
            />
          </div>
        </div>

        <div
          ref={videoSectionWithTitleRef}
          className={`${isVivatechBannerActive() ? 'scroll-mt-36' : 'scroll-mt-16'}`}
        >
          <div
            className={`ease-[cubic-bezier(0.22,1,0.36,1)] overflow-hidden transition-all duration-1000 ${
              showVideoContent
                ? 'mt-8 max-h-96 translate-y-0 scale-100 opacity-100'
                : 'mt-0 max-h-0 translate-y-6 scale-[0.98] opacity-0'
            } `}
          >
            <div className="text-center">
              <h3 className="mb-4 text-3xl font-bold text-gray-900">
                {t('ms4materials.videoSection.title')}
              </h3>

              <p className="mx-auto max-w-3xl text-xl text-gray-600">
                {t('ms4materials.videoSection.description')}
              </p>
            </div>
          </div>
          <div className="bg-gradient-to-b from-gray-50 to-gray-100 pb-16 pt-8">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
              <div ref={videoSectionRef} className="relative">
                <MediaController
                  className="relative overflow-hidden rounded-2xl bg-black shadow-2xl ring-1 ring-black/10"
                  style={
                    {
                      width: '100%',
                      aspectRatio: '1920 / 920',
                      '--media-primary-color': 'white',
                      '--media-secondary-color': 'transparent',
                      '--media-control-background': 'rgba(0,0,0,0.5)',
                    } as React.CSSProperties
                  }
                >
                  <video
                    slot="media"
                    ref={videoRef}
                    className="h-full w-full object-contain"
                    autoPlay
                    loop
                    muted
                    playsInline
                    preload="metadata"
                    id="simulation-video"
                  >
                    <source
                      src="/short-video-physical-deposition.mp4"
                      type="video/mp4"
                    />
                    Your browser does not support the video tag.
                  </video>

                  <MediaControlBar>
                    <MediaPlayButton />
                    <MediaSeekBackwardButton seekOffset={5} />
                    <MediaSeekForwardButton seekOffset={5} />
                    <MediaTimeRange />
                    <MediaFullscreenButton />
                  </MediaControlBar>
                </MediaController>
              </div>

              <div className="bg-gradient-to-br from-white to-blue-50 p-8">
                <p className="mb-6 text-lg text-gray-700">
                  {t('ms4materials.videoSection.videoDescription')}
                </p>
                <div className="mb-8 grid grid-cols-1 gap-6 md:grid-cols-3">
                  <div className="rounded-xl bg-white p-4 shadow-sm transition-shadow hover:shadow-md">
                    <CheckCircle2 className="mb-2 h-8 w-8 text-green-500" />
                    <h5 className="mb-2 font-semibold text-gray-900">
                      {t('ms4materials.videoSection.layerAnalysis.title')}
                    </h5>
                    <p className="text-sm text-gray-600">
                      {t('ms4materials.videoSection.layerAnalysis.description')}
                    </p>
                  </div>
                  <div className="rounded-xl bg-white p-4 shadow-sm transition-shadow hover:shadow-md">
                    <CheckCircle2 className="mb-2 h-8 w-8 text-green-500" />
                    <h5 className="mb-2 font-semibold text-gray-900">
                      {t('ms4materials.videoSection.realTimeDynamics.title')}
                    </h5>
                    <p className="text-sm text-gray-600">
                      {t(
                        'ms4materials.videoSection.realTimeDynamics.description'
                      )}
                    </p>
                  </div>
                  <div className="rounded-xl bg-white p-4 shadow-sm transition-shadow hover:shadow-md">
                    <CheckCircle2 className="mb-2 h-8 w-8 text-green-500" />
                    <h5 className="mb-2 font-semibold text-gray-900">
                      {t(
                        'ms4materials.videoSection.substrateInteraction.title'
                      )}
                    </h5>
                    <p className="text-sm text-gray-600">
                      {t(
                        'ms4materials.videoSection.substrateInteraction.description'
                      )}
                    </p>
                  </div>
                </div>
                <div className="rounded-xl border border-blue-100 bg-blue-50 p-6">
                  <p className="flex items-center font-medium text-blue-900">
                    <Info className="mr-2 h-5 w-5 text-blue-600" />
                    {t('ms4materials.videoSection.info')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Categories Carousel */}
        <div
          ref={solutionsSection}
          className={`mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 ${isVivatechBannerActive() ? 'scroll-mt-36' : 'scroll-mt-16'}`}
        >
          <div className="mb-16 text-center">
            <h2 className="mb-4 text-3xl font-bold text-gray-900">
              {t('ms4materials.comprehensiveSolutions.title')}
            </h2>
            <p className="mx-auto max-w-3xl text-xl text-gray-600">
              {t('ms4materials.comprehensiveSolutions.description')}
            </p>
          </div>
          <div className="relative">
            <div className="flex items-center justify-center">
              <button
                onClick={() => handleCategoryChange('left')}
                className="absolute left-0 z-10 rounded-full bg-white/90 p-2 shadow-lg transition-all hover:bg-white disabled:opacity-50"
                disabled={isAnimating}
              >
                <ChevronLeft className="h-6 w-6 text-gray-800" />
              </button>

              <div className="relative w-full overflow-hidden px-12">
                {categories.map((category, index) => (
                  <div
                    key={index}
                    className={`transition-all duration-500 ease-in-out ${getSlideClassName(
                      index
                    )} absolute left-0 top-0 w-full ${
                      index === currentCategory ? 'relative' : 'absolute'
                    }`}
                  >
                    <h3 className="mb-12 text-center text-2xl font-bold">
                      {category.title}
                    </h3>
                    <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
                      {category.features.map((feature, featureIndex) => (
                        <div
                          key={featureIndex}
                          className={`group relative transform overflow-hidden rounded-lg bg-white p-6 shadow-md transition-all duration-500 hover:shadow-xl ${
                            index === currentCategory
                              ? 'translate-y-0 scale-100 opacity-100'
                              : 'translate-y-4 scale-95 opacity-0'
                          }`}
                          style={{
                            transitionDelay: `${featureIndex * 100}ms`,
                          }}
                        >
                          {/* Background Image with Blur Effect */}
                          <div
                            className="absolute inset-0 opacity-0 transition-opacity duration-500 group-hover:opacity-10"
                            style={{
                              backgroundImage: `url(${feature.image})`,
                              backgroundSize: 'cover',
                              backgroundPosition: 'center',
                              filter: 'blur(8px)',
                            }}
                          />

                          {/* Content */}
                          <div className="relative z-10">
                            {feature.icon}
                            <h4 className="mb-3 text-xl font-semibold text-gray-900 transition-colors group-hover:text-blue-600">
                              {feature.name}
                            </h4>
                            <p className="text-gray-600 transition-colors group-hover:text-gray-800">
                              {feature.description}
                            </p>
                          </div>

                          {/* Hover Effect Overlay */}
                          <div className="pointer-events-none absolute inset-0 bg-gradient-to-tr from-blue-50/50 via-transparent to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <button
                onClick={() => handleCategoryChange('right')}
                className="absolute right-0 z-10 rounded-full bg-white/90 p-2 shadow-lg transition-all hover:bg-white disabled:opacity-50"
                disabled={isAnimating}
              >
                <ChevronRight className="h-6 w-6 text-gray-800" />
              </button>
            </div>

            {/* Dots Navigation */}
            <div className="mt-8 flex justify-center space-x-2">
              {categories.map((_, index) => (
                <button
                  key={index}
                  onClick={() => {
                    const direction =
                      index > currentCategory ? 'right' : 'left';
                    handleCategoryChange(direction, index);
                  }}
                  className={`h-3 w-3 rounded-full transition-all ${
                    index === currentCategory
                      ? 'w-6 bg-blue-600'
                      : 'bg-gray-300 hover:bg-gray-400'
                  }`}
                  disabled={isAnimating}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Key Benefits Section */}
        <div className="bg-white py-16">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="mb-12 text-center">
              <Info className="mx-auto mb-4 h-12 w-12 text-blue-600" />
              <h3 className="text-2xl font-bold text-gray-900">
                {t('ms4materials.keyBenefits.title')}
              </h3>
              <p className="mx-auto mt-4 max-w-2xl text-gray-600">
                {t('ms4materials.keyBenefits.description')}
              </p>
            </div>
            <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-4">
              <div className="group rounded-lg bg-gradient-to-br from-blue-50 to-blue-100 p-6 transition-all duration-300 hover:scale-105 hover:shadow-lg">
                <h4 className="mb-2 text-lg font-semibold text-blue-800 group-hover:text-blue-600">
                  {t('ms4materials.keyBenefits.rapidPrototyping.title')}
                </h4>
                <p className="text-gray-600">
                  {t('ms4materials.keyBenefits.rapidPrototyping.description')}
                </p>
              </div>
              <div className="group rounded-lg bg-gradient-to-br from-blue-50 to-blue-100 p-6 transition-all duration-300 hover:scale-105 hover:shadow-lg">
                <h4 className="mb-2 text-lg font-semibold text-blue-800 group-hover:text-blue-600">
                  {t('ms4materials.keyBenefits.costEfficiency.title')}
                </h4>
                <p className="text-gray-600">
                  {t('ms4materials.keyBenefits.costEfficiency.description')}
                </p>
              </div>
              <div className="group rounded-lg bg-gradient-to-br from-blue-50 to-blue-100 p-6 transition-all duration-300 hover:scale-105 hover:shadow-lg">
                <h4 className="mb-2 text-lg font-semibold text-blue-800 group-hover:text-blue-600">
                  {t('ms4materials.keyBenefits.performanceOptimization.title')}
                </h4>
                <p className="text-gray-600">
                  {t(
                    'ms4materials.keyBenefits.performanceOptimization.description'
                  )}
                </p>
              </div>
              <div className="group rounded-lg bg-gradient-to-br from-blue-50 to-blue-100 p-6 transition-all duration-300 hover:scale-105 hover:shadow-lg">
                <h4 className="mb-2 text-lg font-semibold text-blue-800 group-hover:text-blue-600">
                  {t('ms4materials.keyBenefits.timeToMarket.title')}
                </h4>
                <p className="text-gray-600">
                  {t('ms4materials.keyBenefits.timeToMarket.description')}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="py-8">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <p className="text-center text-gray-600">
              Molecular Simulation For All S.A.S, registered 980 087 845 R.C.S.
              Orléans
            </p>
          </div>
        </div>

        <Footer />
      </div>
    </>
  );
}

export default Materials;
